import { Injectable, inject } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TicketClientApiService } from 'src/app/core/service/apis/tickets/ticket-client-api.service';
import { UserClaimsService } from 'src/app/shared/service/session/user-claims.service';
import { Ticket } from 'src/app/shared/models/tickets/ticket.model';
import { INVERSE_TICKET_FROM_CLIENT_STATUS_DICTIONARY } from 'src/app/shared/constants/crece-digital.constants';
import { TicketMapperService } from 'src/app/shared/service/ticket-client/tickets-mapper.service';
import { DocumentApiService } from 'src/app/core/service/apis/files/document-api.service';
import { DOMAIN_TYPE_SUPPORT } from 'src/app/shared/models/documents/documents.model';
import { AttachmentType } from 'src/app/shared/constants/attachment-type.constants';
import { TicketFieldUpdateService } from 'src/app/shared/service/ticket-client/ticket-field-update.service';

/**
 * Base compartida de `capabilities/ticket-from-client`/`profiles/pivot`:
 * detalle del ticket "desde cliente". `toTicketDetail()` queda por subclase.
 */
@Injectable()
export abstract class TicketFromClientDetailBase {
  protected readonly ticketClientApi = inject(TicketClientApiService);
  protected readonly userClaimsService = inject(UserClaimsService);
  protected readonly detailMapper = inject(TicketMapperService);
  protected readonly documentApi = inject(DocumentApiService);
  protected readonly fieldUpdate = inject(TicketFieldUpdateService);

  /** Detalle + adjuntos, cada uno por su propio recurso. */
  getDetail(recordId: string): Observable<Ticket | null> {
    return this.loadDetail(recordId, false);
  }

  getDetailSilent(recordId: string): Observable<Ticket | null> {
    return this.loadDetail(recordId, true);
  }

  private loadDetail(recordId: string, silent: boolean): Observable<Ticket | null> {
    const options = silent ? { silent: true } : undefined;

    return forkJoin({
      detail: this.ticketClientApi.getTicketDetail(recordId, options),
      docs: this.documentApi.searchDocuments({
        domainType: DOMAIN_TYPE_SUPPORT, recordId, attachmentType: AttachmentType.ADJUNTOS,
      }),
    }).pipe(
      map(({ detail, docs }) => (detail?.ticketRecordId
        ? {
            ...this.detailMapper.mapTicketDetail(detail, 'from-client'),
            attachedDocument: this.detailMapper.mapTicketDocuments(docs),
          }
        : null)),
    );
  }

  // Único endpoint de edición: `update-string-field`, sin fallback legacy. Los campos sin
  // `fieldName` confirmado usan su nombre del front como placeholder.
  updateField(recordId: string, field: string, value: any): Observable<{ recordIdTicket: string }> {
    if (field === 'status') {
      return this.updateStatus(recordId, value);
    }

    return this.fieldUpdate.updateField(recordId, field, value).pipe(
      map(() => ({ recordIdTicket: recordId })),
    );
  }

  private updateStatus(recordId: string, newStatusLabel: string): Observable<{ recordIdTicket: string }> {
    const actorType = this.actorType();

    return this.ticketClientApi.updateTicketStatus(recordId, {
      reportedBy: actorType,
      actorType,
      actionType: 'STATUS_CHANGE',
      newStatus: INVERSE_TICKET_FROM_CLIENT_STATUS_DICTIONARY[newStatusLabel] ?? newStatusLabel,
    }).pipe(
      map(() => ({ recordIdTicket: recordId })),
    );
  }

  /** Quién origina el cambio, según el perfil que tenga la sesión. */
  private actorType(): string {
    return this.userClaimsService.getGroup()?.includes('NOTARIA') ? 'NOTARY' : 'CC_AGENT';
  }
}
